import React, { useEffect, useState } from "react";

import {
    Control,
    UseFormHandleSubmit,
    UseFormResetField,
    UseFormSetValue,
} from "react-hook-form";

import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import LoadingIndicator from "../../../../shared/LoadingIndicator";
import Button from "../../../../shared/NewForms/Button";
import NewHeadline from "../../../../shared/NewHeadline";
import PdfModal from "../../../../shared/PdfModal";
import useSnackbar from "../../../../shared/Snackbar/store";
import Checkbox, { CheckboxContainer } from "../../components/Checkbox";
import useCartStore from "../../ShoppingCart/store";
import StepDeliveryForm from "../StepDeliveryForm";
import StepPaymentMethod from "../StepPaymentMethod";
import useCheckoutStore, { iCheckoutForm } from "../store";
import CartSummary from "./CartSummary";
import StepEditModal from "./EditModal";
import FormSummary from "./FormSummary";

interface iStepSummaryProps {
    children: React.ReactNode;
    control: Control<iCheckoutForm>;
    onSubmit(): void;
    handleSubmit: UseFormHandleSubmit<iCheckoutForm>;
    resetField: UseFormResetField<iCheckoutForm>;
    setValue: UseFormSetValue<iCheckoutForm>;
}

const MainContainer = styled.main`
    display: flex;
    gap: 3em;
    margin-top: 3em;
    ${({ theme }) => theme.breakpoints.upTo.medium.css`
        flex-wrap: wrap;
    `}
`;

export const SubTitle = styled.span`
    font-size: large;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.red.toString()};
`;

export const InlineFlex = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5em;
    align-items: center;
`;

const EditControls = styled.div`
    display: flex;
    justify-content: end;
    margin-top: 1em;
`;

const CheckboxRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 1em;
    align-items: center;
    margin-top: 20px;

    a {
        display: inline-block;
    }
`;

const Label = styled.div`
    flex-grow: 1;
`;

export const TermsOfServiceModalActivator = styled.span`
    all: unset;
    text-decoration: underline;
    cursor: pointer;
`;

export enum EditStepEnum {
    none = "",
    payment = "payment",
    address = "address",
}

const defaultModalData = {
    documentPath: "",
    title: "",
    isVisible: false,
};

const StepSummary = ({
    children,
    control,
    onSubmit,
    handleSubmit,
    resetField,
    setValue,
}: iStepSummaryProps) => {
    const [editStep, setEditStep] = useState<EditStepEnum>(EditStepEnum.none);
    const [modalData, setModalData] = useState(defaultModalData);
    const { notify } = useSnackbar();
    const { isCartLoading, cart, fetchCart } = useCartStore(
        (state) => ({
            isCartLoading: state.loading,
            cart: state.cart,
            fetchCart: state.fetchCart,
        }),
        shallow
    );

    const { setForm } = useCheckoutStore((state) => ({
        setForm: state.setForm,
    }));

    //Always fetch a new cart before displaying the final summary
    useEffect(() => {
        void fetchCart();
    }, []);

    const handleAddressChange = (form: iCheckoutForm) => {
        setForm(form);
        setEditStep(EditStepEnum.none);
        notify("Erfolgreich", "Die Adresse wurde erfolgreich geändert.");
    };

    const handlePaymentChange = async (event: React.BaseSyntheticEvent) => {
        event.preventDefault();
        setEditStep(EditStepEnum.none);
    };

    if (isCartLoading) {
        return <LoadingIndicator />;
    }

    const showTermsModal = (
        event: React.BaseSyntheticEvent,
        documentPath: string,
        title: string
    ) => {
        event.preventDefault();
        setModalData({
            documentPath: documentPath,
            title: title,
            isVisible: true,
        });
    };

    return (
        <>
            <StepEditModal
                isActive={!!editStep}
                handleClose={() => setEditStep(EditStepEnum.none)}
            >
                {/* Show Modal Content depending on the selected Step / Type */}
                {editStep === EditStepEnum.payment && (
                    <StepPaymentMethod>
                        <EditControls>
                            <Button onClick={handlePaymentChange}>
                                Schließen
                            </Button>
                        </EditControls>
                    </StepPaymentMethod>
                )}
                {editStep === EditStepEnum.address && (
                    <StepDeliveryForm
                        onSubmit={handleAddressChange}
                        control={control}
                        handleSubmit={handleSubmit}
                        resetField={resetField}
                        setValue={setValue}
                    >
                        <EditControls>
                            <Button type="submit">Speichern</Button>
                        </EditControls>
                    </StepDeliveryForm>
                )}
            </StepEditModal>

            <NewHeadline>Bestelldetails</NewHeadline>
            <MainContainer>
                {cart && cart.lineItems.length > 0 && (
                    <CartSummary
                        cartItems={cart.lineItems}
                        cartPrice={cart.price}
                        cartDelivery={cart.deliveries[0]}
                    />
                )}

                {/*Open Modal depending on what the user wants to edit*/}
                <FormSummary
                    onEditPayment={() => setEditStep(EditStepEnum.payment)}
                    onEditAddress={() => setEditStep(EditStepEnum.address)}
                />
            </MainContainer>
            {/* Hide the Form when the Form Modal is visible because the modal tries to submit this form even if the forms are not nested.*/}
            {!editStep && (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <CheckboxContainer>
                        <PdfModal
                            title={modalData.title}
                            pdfPath={modalData.documentPath}
                            pdfScale={1.25}
                            isVisible={modalData.isVisible}
                            onClose={() => setModalData(defaultModalData)}
                        />
                        <CheckboxRow>
                            <div>
                                <Checkbox
                                    name="acceptedTermsOfService"
                                    control={control}
                                    rules={{
                                        required:
                                            "Die AGB müssen akzeptiert werden.",
                                    }}
                                />
                            </div>
                            <Label>
                                <span>
                                    Ich erkenne die{" "}
                                    <TermsOfServiceModalActivator
                                        onClick={(event) =>
                                            showTermsModal(
                                                event,
                                                "/img/Hoyer_AGB_Unternehmensgruppe.pdf",
                                                "AGB"
                                            )
                                        }
                                    >
                                        AGB
                                    </TermsOfServiceModalActivator>{" "}
                                    an und habe die{" "}
                                    <TermsOfServiceModalActivator
                                        onClick={(event) =>
                                            showTermsModal(
                                                event,
                                                "/img/11.RET-XXX.FB001_Widerrufsbelehrung.pdf",
                                                "Widerrufsbelehrung"
                                            )
                                        }
                                    >
                                        Widerrufsbelehrung
                                    </TermsOfServiceModalActivator>{" "}
                                    gelesen.
                                </span>
                            </Label>
                        </CheckboxRow>

                        <Checkbox name="acceptedNewsletter" control={control}>
                            Ich stimme zu, dass die Wilhelm Hoyer B.V. & Co. KG
                            mir per E-Mail Newsletter mit Informationen über
                            neue Produkte, Veranstaltungen und Angebote von der
                            Hoyer Unternehmensgruppe zusendet und mich in den
                            Newsletter-Verteilerkreis aufnimmt. Zum Zweck des
                            Versands beauftragt die Wilhelm Hoyer B.V. & Co. KG
                            einen Dienstleister. Dort werden für die Dauer des
                            Versandes der elektronischen Informationen meine
                            Emailadresse und mein Name kurzfristig hochgeladen.
                        </Checkbox>
                    </CheckboxContainer>
                    {children}
                </form>
            )}
        </>
    );
};

export default StepSummary;
